/**
 * Staff API Service
 *
 * Handles CRUD operations for organisation staff members.
 * All endpoints are scoped to the authenticated user's organisation.
 *
 * Endpoints:
 *   POST   /api/organisation/mem           — bulk create members
 *   GET    /api/organisation/mem           — list all members
 *   PUT    /api/organisation/mem/:memberId — update a member
 *   DELETE /api/organisation/mem/:memberId — delete (disable) a member
 *   GET    /api/organisation/providers/assignable?role= — providers the caller may pick
 */

import apiClient from './apiClient';
import { normalizeIndianMobile } from '../../utils/phone';
import eventBus from '../realtime/EventBus';
import { normaliseSpecialisation } from '../../constants/staff';

// ── Staff-roster session cache ───────────────────────────────────────
// The full roster (GET /mem, no role filter) is fetched by ~14 dialog/
// picker components, each on mount — measured ~40+ identical calls in a
// few minutes, a big chunk of the default-tier (120/min) pressure.
// It's static within a session, so we cache it once and refresh it only
// when staff actually change:
//   • local mutations below call invalidateStaffRoster()
//   • cross-device changes arrive as STAFF_UPDATED / DOCTOR_* WS events
//   • a 5-min backstop TTL self-heals if an event is ever dropped
// (Distinct from visitApi.fetchDoctors → /mem?role=DOCTOR, already
// coalesced — that one is left untouched.)
let _rosterPromise = null;   // in-flight GET (single-flight)
let _rosterCache = null;     // last successful { success, data }
let _rosterAt = 0;           // timestamp of last success
const ROSTER_TTL_MS = 5 * 60 * 1000;

// Server-scoped provider pickers (GET /providers/assignable?role=) — one
// request per role per session, same single-flight + TTL pattern as the roster,
// keyed by role (+ purpose). Dropped together with the roster on any staff change.
const _assignableInflight = new Map();   // key -> in-flight promise
const _assignableCache = new Map();      // key -> { result, at }

/** Drop the cached roster so the next fetchStaffList() refetches fresh. */
export const invalidateStaffRoster = () => {
  _rosterCache = null;
  _rosterAt = 0;
  _assignableCache.clear();
};

/** Full reset (logout / account switch) — also clears any in-flight. */
export const resetStaffRoster = () => {
  _rosterPromise = null;
  _rosterCache = null;
  _rosterAt = 0;
  _assignableInflight.clear();
  _assignableCache.clear();
};

// Cross-device freshness: a staff change on another client clears the cache
// so this client refetches on next access. STAFF_UPDATED is now broadcast by
// the backend on create/update/disable/enable; the DOCTOR_* events cover
// availability/status toggles.
['STAFF_UPDATED', 'DOCTOR_STATUS_CHANGED', 'DOCTOR_AVAILABILITY_CHANGED'].forEach((evt) => {
  try {
    eventBus.on(evt, invalidateStaffRoster);
  } catch {
    /* eventBus unavailable (e.g. SSR/test) — backstop TTL still applies */
  }
});

/**
 * Normalize a single member payload before sending to API.
 * @param {Object} member
 * @returns {Object}
 */
function normalizeMemberPayload(member) {
  let mobile = member.mobile;
  try {
    const { e164 } = normalizeIndianMobile(mobile);
    mobile = e164;
  } catch {
    // Validation should catch invalid numbers before this point
  }

  const payload = {
    first_name: (member.first_name || '').trim(),
    last_name: (member.last_name || '').trim(),
    mobile,
    email: (member.email || '').trim().toLowerCase() || undefined,
    role: member.role,
  };

  // Include specialization for doctors
  if (member.role === 'DOCTOR' && member.specialization) {
    payload.specialization = normaliseSpecialisation(member.specialization);
  }

  // Include registration number for doctors (optional)
  if (member.role === 'DOCTOR' && member.registration_no) {
    payload.registration_no = member.registration_no.trim();
  }

  // Include role_name for OTHER_STAFF
  if (member.role === 'OTHER_STAFF' && member.role_name) {
    payload.role_name = member.role_name.trim();
  }

  // Include registration number + authority for LAB_TECHNICIAN (both optional)
  if (member.role === 'LAB_TECHNICIAN') {
    if (member.registration_number) {
      payload.registration_number = member.registration_number.trim();
    }
    if (member.registration_authority) {
      payload.registration_authority = member.registration_authority.trim();
    }
  }

  // Optional department assignment (any non-Admin role). Send the dept_id ONLY
  // when a department was picked; omit the key entirely when blank so the
  // payload is byte-identical to today (backend treats absence as unassigned).
  if (member.department_id) {
    payload.department_id = member.department_id;
  }

  return payload;
}

/**
 * Bulk-create multiple staff members in a single request.
 *
 * POST /api/organisation/mem
 *
 * @param {Array<Object>} memberList - Array of member form data
 * @returns {Promise<{ success: boolean, data?: Object, error?: string }>}
 *
 * Response shape:
 *   { created: [...], failed: [], staff_onboarded: true }
 *   Each created item includes: member_id, username, temporary_password
 */
export const bulkCreateStaff = async (memberList) => {
  try {
    const payload = {
      member: memberList.map(normalizeMemberPayload),
    };

    const response = await apiClient.post('/api/organisation/mem', payload);

    invalidateStaffRoster(); // roster changed → next fetch refetches
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('[StaffAPI] Bulk create error:', error);
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to add staff members. Please try again.'),
    };
  }
};

/**
 * Fetch all staff members for the current organisation.
 *
 * GET /api/organisation/mem
 *
 * Response is an array of member objects with:
 *   username, first_name, last_name, mobile, email, role, status, created_at, updated_at
 *
 * @returns {Promise<{ success: boolean, data?: Array, error?: string }>}
 */
export const fetchStaffList = async () => {
  // Serve from cache while fresh (invalidated on any staff change).
  if (_rosterCache && Date.now() - _rosterAt < ROSTER_TTL_MS) {
    return _rosterCache;
  }
  // Collapse concurrent callers (the ~14 dialogs) onto one request.
  if (_rosterPromise) return _rosterPromise;

  _rosterPromise = (async () => {
    try {
      const response = await apiClient.get('/api/organisation/mem');

      // Response is a direct array
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.member || response.data?.members || response.data?.staff || [];

      const result = { success: true, data };
      _rosterCache = result; // cache only successful results
      _rosterAt = Date.now();
      return result;
    } catch (error) {
      console.error('[StaffAPI] Fetch list error:', error);
      // Do NOT cache failures — next call retries.
      return {
        success: false,
        error: extractErrorMessage(error, 'Failed to load staff list.'),
      };
    } finally {
      _rosterPromise = null;
    }
  })();
  return _rosterPromise;
};

/**
 * Providers of one role the CALLER may pick in a doctor / nurse / lab-tech
 * picker — scoped by the SERVER (department wall, PROVIDER_PICK); the client
 * never re-derives the rule. Items are the same shape as GET /mem.
 *
 * GET /api/organisation/providers/assignable?role=DOCTOR|NURSE|LAB_TECHNICIAN
 *     [&purpose=emergency]  (opt-in: the org-wide roster for a casualty admission)
 *
 * Single-flight + session cache per role (+ purpose); failures are never cached.
 * Names elsewhere still resolve org-wide from fetchStaffList / visitStore.
 *
 * @param {string} role - a STAFF_ROLES value (DOCTOR | NURSE | LAB_TECHNICIAN)
 * @param {string} [purpose] - an ASSIGNABLE_PURPOSE value; omitted = the scoped default list
 * @returns {Promise<{ success: boolean, data?: Array, error?: string }>}
 */
export const fetchAssignableProviders = async (role, purpose) => {
  const key = purpose ? `${role}:${purpose}` : role;
  const hit = _assignableCache.get(key);
  if (hit && Date.now() - hit.at < ROSTER_TTL_MS) return hit.result;
  const inflight = _assignableInflight.get(key);
  if (inflight) return inflight;

  const load = async () => {
    try {
      const response = await apiClient.get('/api/organisation/providers/assignable', {
        params: purpose ? { role, purpose } : { role },
      });
      const result = { success: true, data: Array.isArray(response.data) ? response.data : [] };
      _assignableCache.set(key, { result, at: Date.now() });
      return result;
    } catch (error) {
      console.error('[StaffAPI] Fetch assignable providers error:', error);
      return {
        success: false,
        error: extractErrorMessage(error, 'Failed to load providers.'),
      };
    }
  };
  // Release the in-flight slot only AFTER it was registered (finally callbacks
  // run in a microtask, never synchronously inside load()).
  const promise = load().finally(() => {
    if (_assignableInflight.get(key) === promise) _assignableInflight.delete(key);
  });
  _assignableInflight.set(key, promise);
  return promise;
};

/**
 * Update a staff member.
 *
 * PUT /api/organisation/mem/:memberId
 *
 * @param {string} memberId
 * @param {Object} updates - Partial member data
 * @returns {Promise<{ success: boolean, data?: Object, error?: string }>}
 */
export const updateStaff = async (memberId, updates) => {
  try {
    const payload = normalizeMemberPayload({ ...updates });
    const response = await apiClient.put(`/api/organisation/mem/${memberId}`, payload);

    invalidateStaffRoster();
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('[StaffAPI] Update error:', error);
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to update staff member.'),
    };
  }
};

/**
 * Disable a staff member (soft-delete — preserves record, blocks login).
 *
 * DELETE /api/organisation/mem/:memberId
 *
 * @param {string} memberId
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const disableStaff = async (memberId) => {
  try {
    await apiClient.delete(`/api/organisation/mem/${memberId}`);

    invalidateStaffRoster();
    return { success: true };
  } catch (error) {
    console.error('[StaffAPI] Disable error:', error);
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to disable staff member.'),
    };
  }
};

/**
 * Re-enable a previously disabled staff member.
 * Returns new temporary credentials for the staff member.
 *
 * PATCH /api/organisation/mem/:memberId/enable
 *
 * @param {string} memberId
 * @returns {Promise<{ success: boolean, data?: Object, error?: string }>}
 */
export const enableStaff = async (memberId) => {
  try {
    const response = await apiClient.patch(`/api/organisation/mem/${memberId}/enable`);

    invalidateStaffRoster();
    return {
      success: true,
      data: response.data?.credentials || null,
    };
  } catch (error) {
    console.error('[StaffAPI] Enable error:', error);
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to enable staff member.'),
    };
  }
};

/**
 * Fetch temporary credentials for all staff members.
 *
 * GET /api/organisation/mem/temp
 *
 * @returns {Promise<{ success: boolean, data?: Array, error?: string }>}
 */
export const fetchStaffCredentials = async () => {
  try {
    const response = await apiClient.get('/api/organisation/mem/temp');

    const data = Array.isArray(response.data)
      ? response.data
      : response.data?.created || response.data?.credentials || response.data?.member || [];

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('[StaffAPI] Fetch credentials error:', error);
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to load staff credentials.'),
    };
  }
};

/**
 * Reset a staff member's password to a new temporary password.
 * Username remains the same — only password is changed.
 *
 * PATCH /api/organisation/mem/:memberId/reset-password
 *
 * @param {string} memberId
 * @returns {Promise<{ success: boolean, data?: Object, error?: string }>}
 */
export const resetStaffPassword = async (memberId) => {
  try {
    const response = await apiClient.patch(`/api/organisation/mem/${memberId}/reset-password`);

    return {
      success: true,
      data: response.data?.credentials || null,
    };
  } catch (error) {
    console.error('[StaffAPI] Reset password error:', error);
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to reset password.'),
    };
  }
};

/**
 * Upload a document for a staff member.
 * The file is compressed server-side and stored in S3.
 *
 * POST /api/organisation/mem/:memberId/documents
 *
 * @param {string} memberId
 * @param {File} file - browser File object
 * @returns {Promise<{ success: boolean, data?: Object, error?: string }>}
 */
export const uploadStaffDocument = async (memberId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(
      `/api/organisation/mem/${memberId}/documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('[StaffAPI] Upload document error:', error);
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to upload document.'),
    };
  }
};

/**
 * Get a presigned download URL for a staff document.
 *
 * GET /api/organisation/mem/:memberId/documents/:docIndex/url
 *
 * @param {string} memberId
 * @param {number} docIndex
 * @returns {Promise<{ success: boolean, url?: string, error?: string }>}
 */
export const getStaffDocumentUrl = async (memberId, docIndex) => {
  try {
    const response = await apiClient.get(
      `/api/organisation/mem/${memberId}/documents/${docIndex}/url`
    );

    return {
      success: true,
      url: response.data?.url,
    };
  } catch (error) {
    console.error('[StaffAPI] Get document URL error:', error);
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to get document URL.'),
    };
  }
};

/**
 * Delete a staff document by index.
 *
 * DELETE /api/organisation/mem/:memberId/documents/:docIndex
 *
 * @param {string} memberId
 * @param {number} docIndex
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const deleteStaffDocument = async (memberId, docIndex) => {
  try {
    await apiClient.delete(
      `/api/organisation/mem/${memberId}/documents/${docIndex}`
    );
    return { success: true };
  } catch (error) {
    console.error('[StaffAPI] Delete document error:', error);
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to delete document.'),
    };
  }
};

/**
 * Update doctor availability status.
 * @param {string} memberId
 * @param {'available' | 'on_hold' | 'closed_today'} availability
 * @returns {Promise<{ success: boolean, availability?: string, error?: string }>}
 */
export const updateDoctorAvailability = async (memberId, availability) => {
  try {
    const response = await apiClient.patch(
      `/api/organisation/mem/${memberId}/availability`,
      { availability }
    );
    invalidateStaffRoster();
    return { success: true, availability: response.data.availability };
  } catch (error) {
    console.error('[StaffAPI] Update availability error:', error);
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to update availability.'),
    };
  }
};

/**
 * Update doctor public profile fields.
 *
 * PUT /api/organisation/mem/:memberId/public-profile
 *
 * @param {string} memberId
 * @param {Object} profileData - { qualification, specialization, experience_years, about, registration_no, consultation_department, display_name, is_publicly_visible }
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const updateDoctorPublicProfile = async (memberId, profileData) => {
  try {
    const response = await apiClient.put(
      `/api/organisation/mem/${memberId}/public-profile`,
      profileData
    );
    invalidateStaffRoster();
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[StaffAPI] Update public profile error:', error);
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to update public profile.'),
    };
  }
};

/**
 * Upload doctor profile picture.
 *
 * POST /api/organisation/mem/:memberId/profile-picture
 *
 * @param {string} memberId
 * @param {File} file - Image file (jpg, jpeg, png, webp)
 * @returns {Promise<{ success: boolean, s3_key?: string, error?: string }>}
 */
export const uploadDoctorProfilePicture = async (memberId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(
      `/api/organisation/mem/${memberId}/profile-picture`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    return {
      success: true,
      s3_key: response.data?.s3_key,
    };
  } catch (error) {
    console.error('[StaffAPI] Upload profile picture error:', error);
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to upload profile picture.'),
    };
  }
};

/**
 * Get a doctor's Rx signature text. The single hydration source the
 * SignatureEditor reads from when an admin opens edit mode for an existing
 * DOCTOR. Text only — an uploaded signature image was deliberately removed
 * (not confirmed legally equivalent to a real digital signature under
 * India's IT Act, 2000).
 *
 * GET /api/organisation/mem/:memberId/signature
 *
 * @param {string} memberId
 * @returns {Promise<{ success: boolean, data?: { signature_html }, error?: string }>}
 */
export const getDoctorSignature = async (memberId) => {
  try {
    const response = await apiClient.get(`/api/organisation/mem/${memberId}/signature`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[StaffAPI] Get signature error:', error);
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to load signature.'),
    };
  }
};

/**
 * Save (or clear, if signatureHtml is empty) a doctor's Rx signature text,
 * plus the uniform font family/size for the whole signature block.
 *
 * PUT /api/organisation/mem/:memberId/signature
 *
 * @param {string} memberId
 * @param {string} signatureHtml
 * @param {{ fontFamily?: 'sans'|'serif'|'mono'|null, fontSize?: number|null }} [options]
 * @returns {Promise<{ success: boolean, data?: { signature_html, font_family, font_size }, error?: string }>}
 */
export const updateDoctorSignature = async (memberId, signatureHtml, options = {}) => {
  const { fontFamily = null, fontSize = null } = options;
  try {
    const response = await apiClient.put(
      `/api/organisation/mem/${memberId}/signature`,
      {
        signature_html: signatureHtml || '',
        font_family: fontFamily || null,
        font_size: fontSize || null,
      }
    );
    invalidateStaffRoster();
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[StaffAPI] Update signature error:', error);
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to save signature.'),
    };
  }
};

// ── Helper ───────────────────────────────────────────────────────────

function extractErrorMessage(error, fallback) {
  // FastAPI returns { detail: "..." }, other APIs may use { message: "..." }
  const serverMsg = error.response?.data?.detail || error.response?.data?.message;
  if (serverMsg) return typeof serverMsg === 'string' ? serverMsg : JSON.stringify(serverMsg);

  switch (error.response?.status) {
    case 400: return 'Invalid data. Please check your information.';
    case 401: return 'Session expired. Please login again.';
    case 403: return 'You do not have permission to perform this action.';
    case 404: return 'Member not found.';
    case 409: return 'A staff member with this mobile or email already exists.';
    case 500: return 'Server error. Please try again later.';
    default: return fallback;
  }
}
